import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock, LogIn, Loader2 } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

const formatError = (detail) => {
  if (!detail) return 'Une erreur est survenue';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const Login = () => {
  const { user, login, checking } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (checking) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success('Connexion réussie');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Identifiants invalides');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 flex items-center justify-center p-4" data-testid="login-page">
      {/* Decorative red glow */}
      <div className="pointer-events-none absolute top-0 right-0 w-96 h-96 bg-red-600/20 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_auto-flip-tracker-1/artifacts/mote16qp_26609.jpg"
            alt="Groupe Auto Prestige"
            className="h-24 mx-auto object-contain drop-shadow-[0_0_20px_rgba(220,38,38,0.5)]"
            style={{ mixBlendMode: 'screen', filter: 'contrast(1.15) brightness(1.15)' }}
          />
          <h1 className="mt-6 text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Espace administrateur
          </h1>
          <p className="mt-1 text-sm text-slate-400">Réservé aux gérants du parc</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-red-500/30 p-8"
          data-testid="login-form"
        >
          <div className="flex items-center gap-2 mb-6">
            <div className="p-2 rounded-lg bg-red-600 text-white shadow-md">
              <Lock size={16} />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Connexion</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="admin@example.com"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mot de passe</label>
              <input
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={submitting}
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 transition-all shadow-lg disabled:opacity-60"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
            {submitting ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2026 Groupe Auto Prestige — Accès réservé
        </p>
      </div>
    </div>
  );
};

export default Login;
